// Match sharing (docs/match-sharing.md): native share sheet where available,
// clipboard fallback elsewhere. The production domain is pending confirmation
// (see match-sharing.md prerequisites) — the URL shape is the contract.
/** `blocked` = the browser refused to open the sheet (lost user activation),
 *  which is recoverable by the caller; `failed` = nothing worked at all. */
export type ShareOutcome = 'shared' | 'copied' | 'failed' | 'blocked'

export function matchShareUrl(id: string): string {
  // Origin-aware: shared links must open the SAME deployment that created
  // them (dev hosting today; the upadelmatch.com player domain later — the
  // path contract `/match/{id}` is what stays fixed).
  return `${window.location.origin}/match/${id}`
}

/**
 * "Cómo llegar" — opens turn-by-turn directions in whatever maps app the phone
 * has. No SDK, no API key, no per-load billing: a URL the OS routes to Google
 * Maps, Apple Maps or the browser. That is deliberately better than embedding a
 * map we would then make the player tap through to navigate.
 *
 * Two upgrades, 2026-08-11:
 *
 * 1. `maps.google.com/?q=` is a SEARCH — it dropped the player on a results
 *    page and made them find and tap "Cómo llegar" themselves. The documented
 *    `maps/dir/?api=1` goes straight to directions. Same data, one fewer tap,
 *    on the screen a player opens when they are already late.
 *
 * 2. `destination_place_id` pins Google's OWN listing for the venue, which is
 *    strictly better than the coordinates we hold. 10 of our 48 places geocoded
 *    to `APPROXIMATE` — the TOWN centroid, not the building (two of them landed
 *    on the identical point in Santa Fe). Sending raw lat/lng would route a
 *    player to the middle of the village; Google's business listing knows where
 *    the club actually is. `destination` stays as the human-readable fallback,
 *    which is what renders when no place id is known.
 */
export function directionsUrl(placeQuery: string, googlePlaceId?: string | null): string {
  const base = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(placeQuery)}`
  return googlePlaceId ? `${base}&destination_place_id=${encodeURIComponent(googlePlaceId)}` : base
}

/**
 * The WhatsApp-vernacular match message — mirrors the standardized club-group
 * format (fecha/hora/pista/precio/nivel) so a shared match reads like the
 * posts players already write by hand.
 */
export function matchShareText(m: {
  place: string
  dayLabel: string
  dateISO: string
  timeLabel: string
  levelMin: number | null
  levelMax: number | null
  position: string
  courtReserved: boolean
  cost?: number | null
  players: unknown[]
  capacity: number
  offAppCompanions?: number
}): string {
  const free = Math.max(m.capacity - m.players.length - (m.offAppCompanions ?? 0), 0)
  const nivel =
    m.levelMin == null || m.levelMax == null
      ? 'Nivel indiferente'
      : `Nivel ${m.levelMin.toFixed(1)}–${Math.min(m.levelMax, 7).toFixed(1)}`
  const falta =
    free === 1
      ? `Falta 1${m.position !== 'Indiferente' ? ` a ${m.position.toLowerCase()}` : ''}`
      : `Faltan ${free}`
  const parts = [
    `⏰ ${m.dayLabel} ${m.timeLabel}`,
    `📍 ${m.place}`,
    m.courtReserved ? '✅ Pista reservada' : '🎾 Falta reservar pista',
    ...(m.cost != null ? [`💰 ${m.cost.toFixed(2).replace('.', ',')} €/jugador`] : []),
    `📉 ${nivel}`,
    `🙋 ${falta}`,
  ]
  return parts.join(' · ')
}

export async function shareMatch(id: string, text: string): Promise<ShareOutcome> {
  const url = matchShareUrl(id)
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: 'uPadelMatch', text, url })
      return 'shared'
    } catch (e) {
      // Two very different failures used to collapse into 'shared':
      //
      //   AbortError     — the player opened the sheet and dismissed it. Done.
      //   NotAllowedError — the browser REFUSED to open it, because
      //                     `navigator.share` needs transient user activation
      //                     and ours had expired (Safari is strict; an await
      //                     between the tap and the call is enough).
      //
      // Reporting the second as 'shared' meant the share window never opened
      // and the app said nothing — which is exactly the failure mode of
      // auto-sharing after create, where a Firestore round-trip sits between
      // the tap and this call. The caller needs to know so it can fall back.
      const name = (e as { name?: string }).name
      return name === 'AbortError' ? 'shared' : 'blocked'
    }
  }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`)
    return 'copied'
  } catch {
    return 'failed'
  }
}
